import { logger } from '../utils/ProfessionalLogger';

/**
 * TestRunner.ts
 * Système de tests automatisés pour l'application
 */

interface TestResult {
  name: string;
  passed: boolean;
  error?: string;
  duration: number;
  timestamp: number;
}

interface TestSuite {
  name: string;
  tests: Test[];
  setup?: () => void | Promise<void>;
  teardown?: () => void | Promise<void>;
}

interface Test {
  name: string;
  fn: () => void | Promise<void>;
  timeout?: number;
}

export class TestRunner {
  private suites: TestSuite[] = [];
  private results: TestResult[] = [];
  private isRunning = false;

  /**
   * Ajoute une suite de tests
   */
  public addSuite(suite: TestSuite): void {
    this.suites.push(suite);
  }

  /**
   * Ajoute un test à la suite actuelle
   */
  public addTest(name: string, fn: () => void | Promise<void>, timeout: number = 5000): void {
    if (this.suites.length === 0) {
      this.addSuite({ name: 'Default Suite', tests: [] });
    }
    const currentSuite = this.suites[this.suites.length - 1];
    currentSuite.tests.push({ name, fn, timeout });
  }

  /**
   * Lance tous les tests
   */
  public async runAllTests(): Promise<TestResult[]> {
    if (this.isRunning) {
      throw new Error('Tests are already running');
    }
    this.isRunning = true;
    this.results = [];
    logger.info('🧪 Starting test suite...');

    for (const suite of this.suites) {
      logger.info(`📋 Running suite: ${suite.name}`);

      // Setup
      if (suite.setup) {
        try {
          await suite.setup();
        } catch (error) {
          logger.error(`❌ Setup failed for suite ${suite.name}:`, error);
        }
      }

      // Run tests
      for (const test of suite.tests) {
        const result = await this.runTest(test);
        this.results.push(result);
        if (result.passed) {
          logger.info(`✅ ${test.name} (${result.duration}ms)`);
        } else {
          logger.error(`❌ ${test.name} (${result.duration}ms): ${result.error}`);
        }
      }

      // Teardown
      if (suite.teardown) {
        try {
          await suite.teardown();
        } catch (error) {
          logger.error(`❌ Teardown failed for suite ${suite.name}:`, error);
        }
      }
    }

    this.isRunning = false;
    this.printSummary();
    return this.results;
  }

  /**
   * Lance un test individuel
   */
  private async runTest(test: Test): Promise<TestResult> {
    const start = performance.now();
    try {
      await Promise.race([
        test.fn(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Test timeout')), test.timeout || 5000)
        ),
      ]);
      const duration = performance.now() - start;
      return {
        name: test.name,
        passed: true,
        duration,
        timestamp: Date.now(),
      };
    } catch (error) {
      const duration = performance.now() - start;
      return {
        name: test.name,
        passed: false,
        error: error instanceof Error ? error.message : String(error),
        duration,
        timestamp: Date.now(),
      };
    }
  }

  /**
   * Affiche le résumé des tests
   */
  private printSummary(): void {
    const total = this.results.length;
    const passed = this.results.filter(r => r.passed).length;
    const failed = total - passed;
    const totalDuration = this.results.reduce((sum, r) => sum + r.duration, 0);

    logger.info('\n📊 Test Summary:');
    logger.info(`Total: ${total}`);
    logger.info(`Passed: ${passed}`);
    logger.info(`Failed: ${failed}`);
    logger.info(`Duration: ${totalDuration.toFixed(2)}ms`);
    logger.info(`Success Rate: ${total > 0 ? ((passed / total) * 100).toFixed(1) : 0}%`);

    if (failed > 0) {
      logger.info('\n❌ Failed Tests:');
      this.results.filter(r => !r.passed).forEach(r => logger.info(`  - ${r.name}: ${r.error}`));
    }
  }

  /**
   * Obtient les résultats des tests
   */
  public getResults(): TestResult[] {
    return [...this.results];
  }

  /**
   * Obtient les statistiques des tests
   */
  public getStats(): {
    total: number;
    passed: number;
    failed: number;
    successRate: number;
    averageDuration: number;
  } {
    const total = this.results.length;
    const passed = this.results.filter(r => r.passed).length;
    const failed = total - passed;
    const averageDuration =
      total > 0 ? this.results.reduce((sum, r) => sum + r.duration, 0) / total : 0;

    return {
      total,
      passed,
      failed,
      successRate: total > 0 ? (passed / total) * 100 : 0,
      averageDuration,
    };
  }

  /**
   * Exporte les résultats en JSON
   */
  public exportResults(): string {
    return JSON.stringify(
      {
        results: this.results,
        stats: this.getStats(),
        timestamp: Date.now(),
      },
      null,
      2
    );
  }
}

/**
 * Tests spécifiques à l'application
 */
export class AppTestSuite {
  private testRunner: TestRunner;

  constructor() {
    this.testRunner = new TestRunner();
    this.setupTests();
  }

  /**
   * Configure tous les tests
   */
  private setupTests(): void {
    this.testRunner.addSuite({
      name: 'Canvas Tests',
      setup: () => this.setupCanvas(),
      teardown: () => this.teardownCanvas(),
      tests: [
        {
          name: 'Canvas initialization',
          fn: () => this.testCanvasInitialization(),
        },
        {
          name: 'Rectangle creation',
          fn: () => this.testRectangleCreation(),
        },
        {
          name: 'Circle creation',
          fn: () => this.testCircleCreation(),
        },
        {
          name: 'Text creation',
          fn: () => this.testTextCreation(),
        },
      ],
    });

    this.testRunner.addSuite({
      name: 'Performance Tests',
      tests: [
        {
          name: 'Memory usage',
          fn: () => this.testMemoryUsage(),
        },
        {
          name: 'Render performance',
          fn: () => this.testRenderPerformance(),
        },
      ],
    });

    this.testRunner.addSuite({
      name: 'Integration Tests',
      tests: [
        {
          name: 'Save/Load functionality',
          fn: () => this.testSaveLoad(),
        },
        {
          name: 'Export functionality',
          fn: () => this.testExport(),
        },
      ],
    });
  }

  /**
   * Lance tous les tests
   */
  public async runTests(): Promise<TestResult[]> {
    return await this.testRunner.runAllTests();
  }

  /**
   * Obtient les résultats
   */
  public getResults(): TestResult[] {
    return this.testRunner.getResults();
  }

  /**
   * Obtient les statistiques
   */
  public getStats() {
    return this.testRunner.getStats();
  }

  // Tests spécifiques
  private async setupCanvas(): Promise<void> {
    // Setup pour les tests de canvas
  }

  private async teardownCanvas(): Promise<void> {
    // Nettoyage après les tests
  }

  private testCanvasInitialization(): void {
    const canvas = document.getElementById('canvas-container');
    if (!canvas) {
      throw new Error('Canvas container not found');
    }
  }

  private testRectangleCreation(): void {
    // Test de création de rectangle
  }

  private testCircleCreation(): void {
    // Test de création de cercle
  }

  private testTextCreation(): void {
    // Test de création de texte
  }

  private testMemoryUsage(): void {
    if ('memory' in performance) {
      interface PerformanceMemory {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      }
      interface PerformanceWithMemory extends Performance {
        memory?: PerformanceMemory;
      }
      const memory = (performance as PerformanceWithMemory).memory;
      if (memory) {
        const usedMemory = memory.usedJSHeapSize;
        const maxMemory = memory.jsHeapSizeLimit;
        if (usedMemory > maxMemory * 0.8) {
          throw new Error(`High memory usage: ${usedMemory} / ${maxMemory}`);
        }
      }
    }
  }

  private testRenderPerformance(): void {
    const start = performance.now();
    for (let i = 0; i < 1000; i++) {
      Math.random();
    }
    const duration = performance.now() - start;
    if (duration > 16) {
      throw new Error(`Render too slow: ${duration}ms`);
    }
  }

  private testSaveLoad(): void {
    const testData = { test: 'data' };
    const saved = JSON.stringify(testData);
    const loaded = JSON.parse(saved);
    if (JSON.stringify(loaded) !== saved) {
      throw new Error('Save/Load data mismatch');
    }
  }

  private testExport(): void {
    // Test d'export
  }
}

// Instance globale du test runner
export const testRunner = new TestRunner();
export const appTestSuite = new AppTestSuite();
